// src/features/doctor-dashboard/DoctorTodayPage.tsx
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { format, parseISO, subMonths } from 'date-fns';
import {
  Play, CheckCircle, RefreshCw, UserCheck, Activity,
  CalendarClock, History, Calendar, AlertCircle,
} from 'lucide-react';
import { doctorDashboardService } from '../../api/services';
import { QueueItemDto, DoctorAppointmentSummary } from '../../api/types';
import { formatAppointmentDate, calcDuration } from '../../utils/formatDate';
import { statusLabel } from '../../utils/statusLabel';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type DoctorView = 'today' | 'upcoming' | 'history';

const STATUS_ORDER = ['CHECKED_IN', 'IN_CONSULTATION', 'CONFIRMED', 'PENDING', 'COMPLETED'];

const SOURCE_COLORS: Record<string, string> = {
  Website: 'bg-blue-50 text-blue-700',
  WalkIn: 'bg-orange-50 text-orange-700',
  WhatsApp: 'bg-green-50 text-green-700',
  Phone: 'bg-purple-50 text-purple-700',
};

const SESSION_COLORS: Record<string, string> = {
  Morning: 'bg-amber-50 text-amber-700',
  Evening: 'bg-indigo-50 text-indigo-700',
};

function useCurrentView(): DoctorView {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (location.pathname === '/doctor/today') return 'today';
  // /doctor/my-patients?tab=history or ?tab=upcoming (default: upcoming)
  const tab = searchParams.get('tab');
  if (tab === 'history') return 'history';
  return 'upcoming';
}

const VIEW_CONFIG: Record<DoctorView, { title: string; subtitle: string; icon: any }> = {
  today: { title: "Today's Patients", subtitle: '', icon: UserCheck },
  upcoming: { title: 'Upcoming Appointments', subtitle: 'Future scheduled appointments', icon: CalendarClock },
  history: { title: 'Appointment History', subtitle: 'Past appointments', icon: History },
};

export function DoctorTodayPage() {
  const view = useCurrentView();
  const config = VIEW_CONFIG[view];
  const Icon = config.icon;
  const qc = useQueryClient();
  const [showAllHistory, setShowAllHistory] = useState(false);

  // ── Today's queue ──────────────────────────────────────────────────
  const { data: patients = [], isLoading: todayLoading, refetch: refetchToday } = useQuery({
    queryKey: ['doctor-today'],
    queryFn: doctorDashboardService.getToday,
    refetchInterval: 20000,
    enabled: view === 'today',
  });

  // ── My patients (upcoming + history) ───────────────────────────────
  const {
    data: myPatients,
    isLoading: myPatientsLoading,
    isError: myPatientsError,
    refetch: refetchMyPatients,
  } = useQuery({
    queryKey: ['doctor', 'my-patients'],
    queryFn: doctorDashboardService.getMyPatients,
    enabled: view === 'upcoming' || view === 'history',
  });

  const { mutate: startConsult } = useMutation({
    mutationFn: (id: number) => doctorDashboardService.startConsultation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-today'] });
      toast.success('Consultation started');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: completeConsult } = useMutation({
    mutationFn: (id: number) => doctorDashboardService.completeConsultation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-today'] });
      toast.success('Consultation completed');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Derived data ───────────────────────────────────────────────────
  const sorted = [...patients].sort((a, b) =>
    STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
    (a.tokenNumber ?? 9999) - (b.tokenNumber ?? 9999)
  );

  const waiting = patients.filter(p => p.status === 'CHECKED_IN').length;
  const inConsult = patients.filter(p => p.status === 'IN_CONSULTATION').length;
  const done = patients.filter(p => p.status === 'COMPLETED').length;

  const upcoming = useMemo(() => {
    if (!myPatients) return [];
    return [...myPatients.futureAppointments].sort((a, b) =>
      (a.appointmentDate ?? '').localeCompare(b.appointmentDate ?? '') ||
      (a.tokenNumber ?? 9999) - (b.tokenNumber ?? 9999)
    );
  }, [myPatients]);

  const history = useMemo(() => {
    if (!myPatients) return [];
    const all = [...myPatients.history].sort((a, b) =>
      (b.appointmentDate ?? '').localeCompare(a.appointmentDate ?? '')
    );
    if (showAllHistory) return all;
    const cutoff = format(subMonths(new Date(), 3), 'yyyy-MM-dd');
    return all.filter(a => !a.appointmentDate || a.appointmentDate >= cutoff);
  }, [myPatients, showAllHistory]);

  const totalHistory = myPatients?.history.length ?? 0;

  const handleRefresh = () => {
    if (view === 'today') refetchToday();
    else refetchMyPatients();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Icon className="w-6 h-6 text-brand-600" />
            {config.title}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {view === 'today'
              ? `${format(new Date(), 'EEEE, MMMM d')} · ${patients.length} total`
              : config.subtitle}
          </p>
        </div>
        <button onClick={handleRefresh} className="btn btn-secondary btn-sm">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* ── Today View ────────────────────────────────────────────────── */}
      {view === 'today' && (
        <>
          {/* Status bar */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Waiting', value: waiting, color: 'text-amber-700 bg-amber-50 border-amber-200' },
              { label: 'In Consultation', value: inConsult, color: 'text-purple-700 bg-purple-50 border-purple-200' },
              { label: 'Completed', value: done, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
            ].map((s) => (
              <div key={s.label} className={clsx('card p-4 border text-center', s.color)}>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs font-medium mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Patient list */}
          {todayLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No patients scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((patient: QueueItemDto) => (
                <div
                  key={patient.appointmentId}
                  className={clsx(
                    'card p-5 transition-all duration-200 animate-fade-up',
                    patient.status === 'IN_CONSULTATION' && 'border-purple-200 bg-purple-50/50',
                    patient.status === 'CHECKED_IN' && 'border-amber-200 bg-amber-50/30',
                    patient.status === 'COMPLETED' && 'opacity-60',
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className={clsx(
                      'w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold flex-shrink-0',
                      patient.status === 'IN_CONSULTATION' ? 'bg-purple-200 text-purple-800' :
                      patient.status === 'CHECKED_IN' ? 'bg-amber-200 text-amber-800' :
                      patient.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-600' :
                      'bg-slate-100 text-slate-500'
                    )}>
                      {patient.tokenNumber ?? '–'}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{patient.patientName}</span>
                        <span className={`status-${patient.status}`}>
                          {statusLabel(patient.status)}
                        </span>
                        {patient.patientGender && (
                          <span className="text-xs text-slate-400">{patient.patientGender}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {patient.patientPhone} · {patient.sessionType}
                      </div>
                      {patient.patientNotes && (
                        <div className="text-xs text-slate-500 mt-1 italic">"{patient.patientNotes}"</div>
                      )}
                      {patient.checkedInAt && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          Arrived {format(parseISO(patient.checkedInAt), 'h:mm a')}
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0">
                      {patient.status === 'CHECKED_IN' && (
                        <button
                          onClick={() => startConsult(patient.appointmentId)}
                          className="btn btn-primary btn-sm"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Start
                        </button>
                      )}
                      {patient.status === 'IN_CONSULTATION' && (
                        <button
                          onClick={() => completeConsult(patient.appointmentId)}
                          className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Upcoming View ─────────────────────────────────────────────── */}
      {view === 'upcoming' && (
        <AppointmentTable
          data={upcoming}
          isLoading={myPatientsLoading}
          isError={myPatientsError}
          onRetry={refetchMyPatients}
          emptyMessage="No upcoming appointments"
          countLabel={`${upcoming.length} upcoming appointment${upcoming.length !== 1 ? 's' : ''}`}
          variant="upcoming"
        />
      )}

      {/* ── History View ──────────────────────────────────────────────── */}
      {view === 'history' && (
        <>
          <AppointmentTable
            data={history}
            isLoading={myPatientsLoading}
            isError={myPatientsError}
            onRetry={refetchMyPatients}
            emptyMessage="No appointment history yet"
            countLabel={`${totalHistory} past appointment${totalHistory !== 1 ? 's' : ''}${!showAllHistory && history.length < totalHistory ? ` (showing last 3 months)` : ''}`}
            variant="history"
          />
          {!showAllHistory && totalHistory > history.length && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowAllHistory(true)}
                className="btn btn-secondary btn-sm"
              >
                Load all ({totalHistory} total)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Shared table component for Upcoming / History ────────────────────────

function AppointmentTable({
  data,
  isLoading,
  isError,
  onRetry,
  emptyMessage,
  countLabel,
  variant,
}: {
  data: DoctorAppointmentSummary[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyMessage: string;
  countLabel: string;
  variant: 'upcoming' | 'history';
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-10 h-10 text-red-300 mx-auto mb-3" />
        <p className="text-slate-500 mb-3">Failed to load data</p>
        <button onClick={onRetry} className="btn btn-secondary btn-sm">
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-16">
        <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-slate-500 mb-3">{countLabel}</p>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Token #</th>
                <th className="px-4 py-3">Session</th>
                <th className="px-4 py-3">Patient Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Gender</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Follow-up</th>
                <th className="px-4 py-3">Status</th>
                {variant === 'history' && <th className="px-4 py-3">Duration</th>}
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((appt) => (
                <tr key={appt.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {appt.appointmentDate
                      ? formatAppointmentDate(appt.appointmentDate)
                      : <span className="text-amber-600 text-xs font-medium">TBC</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-mono">
                    {appt.tokenNumber ?? '–'}
                  </td>
                  <td className="px-4 py-3">
                    {appt.sessionType ? (
                      <span className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                        SESSION_COLORS[appt.sessionType] ?? 'bg-gray-100 text-gray-600'
                      )}>
                        {appt.sessionType}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">–</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium">{appt.patientName}</td>
                  <td className="px-4 py-3 text-gray-700">{appt.patientPhone}</td>
                  <td className="px-4 py-3 text-gray-700">{appt.patientGender ?? '–'}</td>
                  <td className="px-4 py-3">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                      SOURCE_COLORS[appt.bookingSource] ?? 'bg-gray-100 text-gray-600'
                    )}>
                      {appt.bookingSource}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {appt.isFollowUp ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`status-${appt.status}`}>
                      {statusLabel(appt.status)}
                    </span>
                  </td>
                  {variant === 'history' && (
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {calcDuration(appt.consultationStartAt, appt.consultationEndAt)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate" title={appt.patientNotes ?? ''}>
                    {appt.patientNotes
                      ? appt.patientNotes.length > 40
                        ? `${appt.patientNotes.slice(0, 40)}...`
                        : appt.patientNotes
                      : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
